/* eslint-disable css-modules/no-unused-class */
import React from 'react';
import {
  oneOfType,
  oneOf,
  object,
  bool,
  number,
  string,
  array,
} from 'prop-types';
import { useTranslation } from 'react-i18next';
import useStyles from 'hooks/useStyles';
import { useDispatch } from 'react-redux';

import useGetOfficialSiteURL from 'hooks/useGetOfficialSiteURL';
import {
  PURCHASE_PAYMENT_OPTION_ITEM_CLICK,
  PURCHASE_AVAILABLE_LESSON_CLICK,
} from 'constants/gtmEvent';
import { SUBSCRIPTION, ONE_TIME } from 'constants/checkoutCollectionGroupTypes';
import { SINGLE } from 'constants/checkoutCollectionTypes';
import { COURSE, REGULAR_CLASS } from 'constants/contentTypes';
import { AVAILABLE_LESSON } from 'constants/liveEventSections';
import { SINGLE_PURCHASE_TEMPLATE } from 'constants/routingPath';
import { push } from 'reduxModules/navigation/action';
import { sendGTMEvent } from 'reduxModules/googleTagManager/action';

import checkoutCollectionStyles from 'styles/checkoutCollection.scss';
import CountdownDate from 'components/CountdownDate';
import ContentInfo from 'components/ContentInfo';
import Price from '../Price';
import PaymentOptionButton from '../PaymentOptionButton';
import AvailableLessonModal from '../AvailableLessonModal';

import styles from './PaymentOptionEnhanced.scss';

export default function PaymentOptionEnhanced({
  id,
  displayPrice,
  hasPresale,
  plan,
  presaleExpiredAt,
  contentType,
  contentId,
  groupType,
  lessons,
  totalLessonsDurationTime,
  expireDate,
  reviewDays,
  isSemester,
  topicId,
  topicName,
}) {
  useStyles(styles, checkoutCollectionStyles);
  const { t } = useTranslation(['purchase']);
  const getOfficialSiteURL = useGetOfficialSiteURL();
  const dispatch = useDispatch();

  const { name, description, highlight, billingCyclePerMonths } = plan;
  const { originalPrice, sellingPrice, discount } = displayPrice;

  const showPromotionSection = highlight || hasPresale;

  const renderPromotionLabel = () => {
    if (highlight)
      return (
        <div className={checkoutCollectionStyles.labelAccent}>{highlight}</div>
      );
    if (hasPresale)
      return (
        <div className={checkoutCollectionStyles.label}>
          {t('purchase.promoOfferTag.limited')}
        </div>
      );

    return null;
  };

  const onAvaliableLessonClick = () => {
    dispatch(
      sendGTMEvent({
        eventName: PURCHASE_AVAILABLE_LESSON_CLICK,
        data: {
          topicId,
          topicName,
          planId: plan.id,
          planName: plan.name,
        },
      }),
    );
  };

  const handleGoPurchase = () => {
    dispatch(
      sendGTMEvent({
        eventName: PURCHASE_PAYMENT_OPTION_ITEM_CLICK,
        data: {
          type: contentType,
          contentId,
          bundleType: SINGLE,
          checkoutCollectionId: id,
          section: AVAILABLE_LESSON,
        },
      }),
    );

    dispatch(
      push(
        getOfficialSiteURL(
          `${SINGLE_PURCHASE_TEMPLATE}#from=${contentType}:${contentId}`,
          {
            id,
          },
        ),
      ),
    );
  };

  return (
    <div className={styles.root}>
      {showPromotionSection && (
        <div className={checkoutCollectionStyles.promotion}>
          {renderPromotionLabel()}
          {hasPresale && <CountdownDate dateTo={presaleExpiredAt} />}
        </div>
      )}
      <div className={styles.planContent}>
        <div className={styles.infoContent}>
          <h3 className={styles.name}>{name}</h3>
          <ContentInfo
            contentType={contentType}
            isSemester={isSemester}
            isOneTime={groupType === ONE_TIME}
            lessonCount={lessons.length}
            expireDate={expireDate}
            reviewDays={reviewDays}
            totalLessonsDurationTime={totalLessonsDurationTime}
          />
          <div className={styles.description}>{description}</div>
          {contentType === REGULAR_CLASS && groupType !== ONE_TIME && (
            <AvailableLessonModal
              lessons={lessons}
              isSemester={isSemester}
              lastDay={plan.lastDay}
              onPurchaseButtonClick={handleGoPurchase}
              onAvaliableLessonClick={onAvaliableLessonClick}
            />
          )}
        </div>
        <div className={styles.priceContent}>
          <Price
            className={styles.price}
            perMonth={billingCyclePerMonths}
            sellingPrice={sellingPrice}
            originalPrice={originalPrice}
            hasDiscount={!!discount}
            hasUnit={groupType === SUBSCRIPTION}
          />
          {sellingPrice && contentType === COURSE ? (
            <div className={styles.pricePerLesson}>
              {t('purchase.pricingDisplay.coursePricePerLesson', {
                price: displayPrice.singleLessonPrice,
              })}
            </div>
          ) : (
            <div className={styles.pricePerLesson}>
              {t('purchase.pricingDisplay.regularClassPricePerLesson', {
                price: displayPrice.singleLessonPrice,
              })}
            </div>
          )}
        </div>
      </div>
      <PaymentOptionButton
        type={SINGLE}
        checkoutId={id}
        discount={discount}
        isHighlighted={!!highlight}
        contentType={contentType}
        contentId={contentId}
      />
    </div>
  );
}

PaymentOptionEnhanced.propTypes = {
  id: number.isRequired,
  displayPrice: object,
  hasPresale: bool.isRequired,
  plan: object.isRequired,
  presaleExpiredAt: string,
  contentType: oneOf([COURSE, REGULAR_CLASS]).isRequired,
  contentId: oneOfType([string, number]).isRequired,
  groupType: string.isRequired,
  lessons: array.isRequired,
  totalLessonsDurationTime: object.isRequired,
  expireDate: string.isRequired,
  reviewDays: number.isRequired,
  isSemester: bool.isRequired,
  topicId: number.isRequired,
  topicName: string.isRequired,
};

PaymentOptionEnhanced.defaultProps = {
  displayPrice: undefined,
  presaleExpiredAt: undefined,
};
